/**
 * CSRF Protection Service
 * 
 * Manages CSRF tokens for secure API requests.
 * Automatically fetches, stores, and includes CSRF tokens in requests.
 */

import { config } from '../config';

class CSRFService {
  constructor() {
    this.csrfToken = null;
    this.tokenPromise = null;
    this.isInitialized = false;
  }

  /**
   * Get CSRF token from cookie
   * @returns {string|null} CSRF token from cookie
   */
  getTokenFromCookie() {
    const name = 'XSRF-TOKEN=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    
    for (let i = 0; i < cookieArray.length; i++) {
      let cookie = cookieArray[i].trim();
      if (cookie.indexOf(name) === 0) {
        return cookie.substring(name.length, cookie.length);
      }
    }
    return null;
  }

  /**
   * Fetch a new CSRF token from the server
   * @returns {Promise<string>} CSRF token
   */
  async fetchToken() {
    try {
      const response = await fetch(`${config.API_URI}/v1/csrf-token`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch CSRF token: ${response.status}`);
      }

      const data = await response.json();
      this.csrfToken = data.csrfToken;
      this.isInitialized = true;
      
      console.log('✅ CSRF token fetched successfully');
      return this.csrfToken;
    } catch (error) {
      console.error('❌ Failed to fetch CSRF token:', error);
      // Fallback to cookie token if fetch fails
      const cookieToken = this.getTokenFromCookie();
      if (cookieToken) {
        this.csrfToken = cookieToken;
        this.isInitialized = true;
        return cookieToken;
      }
      throw error;
    }
  }

  /**
   * Initialize CSRF protection
   * Fetches token on first call, returns cached token on subsequent calls
   * @returns {Promise<string>} CSRF token
   */
  async initialize() {
    // If already initialized, return cached token
    if (this.isInitialized && this.csrfToken) {
      return this.csrfToken;
    }

    // If a fetch is already in progress, wait for it
    if (this.tokenPromise) {
      return this.tokenPromise;
    }

    // Start fetching the token
    this.tokenPromise = this.fetchToken()
      .finally(() => {
        this.tokenPromise = null;
      });

    return this.tokenPromise;
  }

  /**
   * Get the current CSRF token
   * Tries cookie first, then fetches from server if needed
   * @returns {Promise<string>} CSRF token
   */
  async getToken() {
    // Try to get token from cookie first (server rotates it on each request)
    const cookieToken = this.getTokenFromCookie();
    if (cookieToken) {
      this.csrfToken = cookieToken;
      return cookieToken;
    }

    // If no cookie token, use cached token or fetch new one
    if (this.csrfToken) {
      return this.csrfToken;
    }

    return this.initialize();
  }

  /**
   * Refresh the CSRF token
   * Forces a new token fetch from the server
   * @returns {Promise<string>} New CSRF token
   */
  async refreshToken() {
    this.csrfToken = null;
    this.isInitialized = false;
    return this.fetchToken();
  }

  /**
   * Clear the cached CSRF token
   */
  clearToken() {
    this.csrfToken = null;
    this.isInitialized = false;
  }

  /**
   * Check if CSRF protection should be applied to a request
   * @param {string} method - HTTP method
   * @returns {boolean} Whether CSRF protection is needed
   */
  shouldProtect(method) {
    const safeMethods = ['GET', 'HEAD', 'OPTIONS'];
    return !safeMethods.includes(method?.toUpperCase());
  }

  /**
   * Get headers with CSRF token for a request
   * @param {string} method - HTTP method
   * @param {Object} existingHeaders - Existing headers
   * @returns {Promise<Object>} Headers with CSRF token
   */
  async getHeaders(method, existingHeaders = {}) {
    if (!this.shouldProtect(method)) {
      return existingHeaders;
    }

    try {
      const token = await this.getToken();
      return {
        ...existingHeaders,
        'X-CSRF-Token': token
      };
    } catch (error) {
      console.warn('⚠️ Failed to get CSRF token, proceeding without it:', error);
      return existingHeaders;
    }
  }

  /**
   * Handle CSRF token errors (403 responses)
   * Refreshes token and returns true if should retry
   * @param {Response} response - Fetch response
   * @returns {Promise<boolean>} Whether to retry the request
   */
  async handleError(response) {
    if (response.status === 403) {
      try {
        const errorData = await response.json().catch(() => ({}));
        
        // Check if it's a CSRF error
        if (errorData.error?.includes('CSRF') || errorData.message?.includes('CSRF')) {
          console.warn('⚠️ CSRF token error, refreshing token...');
          await this.refreshToken();
          return true; // Indicate that request should be retried
        }
      } catch (error) {
        console.error('Error parsing CSRF error response:', error);
      }
    }
    return false;
  }
}

// Export singleton instance
export const csrfService = new CSRFService();

// Export class for testing
export default CSRFService;

